/**
 * RechnungPDF
 *
 * Müşteri faturası: fiyat + KDV + IBAN + ödeme şartları. Admin
 * tarafından manuel tetiklenir. Numara RE-YYYY-NNNN formatında.
 */
import { computeTaxGroups } from '@/lib/invoiceCalc';
import { findInvoiceByNo } from '@/lib/invoiceApi';
import {
  fetchPdfSettings,
  downloadPdfFromHtml,
  wrapPdfDocument,
  renderCompanyHeader,
  renderFooter,
  formatDate,
  formatCurrency,
  escapeHtml,
} from '@/lib/pdfRenderer';
import { base44 } from '@/api/base44Client';
import { getOrderItems } from '@/lib/orderFields';
import { getVorlage, textZuHtml } from '@/lib/dokumentVorlagen';

// Eine Berechnung für PDF und Buchung (lib/invoiceCalc.js, = DB-Funktion).
// Positionen dürfen eigene Steuersätze haben (manuelle Rechnungen).
function calculateTotals(items, order, lead) {
  const kundenrabattProzent = Number(lead?.kundenrabatt_prozent) || 0;
  const r = computeTaxGroups(items, { kundenrabattProzent, defaultRate: Number(order.tax_rate ?? 19) });
  return { ...r, kundenrabattProzent };
}

function renderTemplate(order, settings, lead) {
  const today = formatDate(new Date());
  const invoiceAt = order.invoiced_at ? formatDate(order.invoiced_at) : today;
  const items = getOrderItems(order);

  const {
    subtotal, kundenrabattProzent, kundenrabattBetrag, netto, groups, total,
  } = calculateTotals(items, order, lead);

  // Zahlungsbedingungen des Kontakts
  const zahlungszielTage = lead?.zahlungsziel_tage ?? 5;
  const skontoProzent = Number(lead?.skonto_prozent) || 0;
  const skontoTage = Number(lead?.skonto_tage) || 0;
  const hasSkonto = skontoProzent > 0 && skontoTage > 0;
  const skontoBetrag = hasSkonto ? total * (skontoProzent / 100) : 0;

  const rows = items.map((item, idx) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unit_price) || 0;
    const rabatt = Number(item.rabatt_prozent) || 0;
    const line = qty * price * (1 - rabatt / 100);
    return `
      <tr>
        <td style="padding:10px;border-bottom:1px solid #e5e7eb;text-align:center;font-family:monospace">${idx + 1}</td>
        <td style="padding:10px;border-bottom:1px solid #e5e7eb">
          <div style="font-weight:600">${escapeHtml(item.product_name || item.name || '')}</div>
          ${item.variant_label || item.variant_value || item.variant
            ? `<div style="font-size:10px;color:#555">${escapeHtml(item.variant_label || item.variant_value || item.variant)}</div>` : ''}
          ${item.product_sku
            ? `<div style="font-size:10px;color:#999;font-family:monospace">Art-Nr: ${escapeHtml(item.product_sku)}</div>` : ''}
          ${rabatt > 0
            ? `<div style="font-size:10px;color:#c2410c;font-weight:600">Rabatt: ${rabatt} %</div>` : ''}
        </td>
        <td style="padding:10px;border-bottom:1px solid #e5e7eb;text-align:right">${qty}</td>
        <td style="padding:10px;border-bottom:1px solid #e5e7eb;text-align:center;color:#666">${escapeHtml(item.unit || 'Stück')}</td>
        <td style="padding:10px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace">${formatCurrency(price)}</td>
        <td style="padding:10px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace;font-weight:700">${formatCurrency(line)}</td>
      </tr>
    `;
  }).join('');

  const vorlage = getVorlage(settings, 'rechnung');
  const ctx = {
    'kunde.name': order.lead_name || '', 'kunde.nummer': (order.lead_id || '').substring(0, 4).toUpperCase(),
    'firma.name': settings.company_name, 'firma.geschaeftsfuehrer': settings.geschaeftsfuehrer, 'firma.ansprechpartner': settings.ansprechpartner,
    'beleg.nummer': order.invoice_number || '', 'beleg.datum': invoiceAt,
    'rechnung.nummer': order.invoice_number || '', 'rechnung.datum': invoiceAt, 'rechnung.zahlungsziel_tage': zahlungszielTage,
    'bestellung.nummer': order.order_number || '',
  };
  const paymentTermsHtml = textZuHtml(vorlage.zahlungsbedingungen, ctx);
  const thanksHtml = textZuHtml(vorlage.dank, ctx);
  const closingHtml = textZuHtml(vorlage.schluss, ctx);
  const ueberweisungHtml = textZuHtml(vorlage.ueberweisungshinweis, ctx);

  return wrapPdfDocument(`
      ${renderCompanyHeader(settings)}

      <div style="display:flex;justify-content:space-between;margin-bottom:24px">
        <div>
          <h1 style="font-size:24px;margin:0 0 6px 0;color:#172554">${order.invoice_number ? 'RECHNUNG' : 'RECHNUNG — ENTWURF'}</h1>
          <div style="color:#666;font-size:11px">${thanksHtml}</div>
        </div>
        <div style="text-align:right;font-size:11px;color:#333">
          <div style="font-size:14px;font-weight:700;color:#172554">${escapeHtml(order.invoice_number || '(wird vergeben)')}</div>
          <div><strong>Rechnungsdatum:</strong> ${invoiceAt}</div>
          <div><strong>Bestellung:</strong> ${escapeHtml(order.order_number || '')}</div>
          ${(order._invoice?.delivery_date || order.delivered_at) ? `<div><strong>Leistungsdatum:</strong> ${formatDate(order._invoice?.delivery_date || order.delivered_at)}</div>` : ''}
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px;font-size:11px">
        <div style="padding:14px;background:#f3f4f6;border-left:3px solid #D4A017">
          <div style="font-size:9px;color:#155e75;font-weight:700;text-transform:uppercase;margin-bottom:6px">Rechnungsempfänger</div>
          <div style="font-weight:700;font-size:12px">${escapeHtml(order.lead_name || '')}</div>
          ${order.lead_address ? `<div style="color:#555;margin-top:2px">${escapeHtml(order.lead_address)}</div>` : ''}
          ${order.lead_email ? `<div style="color:#555;margin-top:2px">${escapeHtml(order.lead_email)}</div>` : ''}
        </div>
        <div style="padding:14px;background:#f8fafc;border-left:3px solid #64748b">
          <div style="font-size:9px;color:#334155;font-weight:700;text-transform:uppercase;margin-bottom:6px">Zahlungsbedingungen</div>
          <div style="color:#111;font-weight:600">Zahlungsziel: ${zahlungszielTage} Tage</div>
          ${hasSkonto
            ? `<div style="color:#166534;font-weight:600;margin-top:4px">Skonto: ${skontoProzent} % bei Zahlung innerhalb ${skontoTage} Tagen (−${formatCurrency(skontoBetrag)})</div>`
            : ''}
          ${paymentTermsHtml ? `<div style="color:#555;margin-top:4px">${paymentTermsHtml}</div>` : ''}
          ${order.zahlungsart ? `<div style="color:#555;margin-top:4px">Zahlungsart: ${escapeHtml(order.zahlungsart)}</div>` : ''}
        </div>
      </div>

      <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:24px">
        <thead>
          <tr style="background:#172554;color:#fff">
            <th style="padding:10px;text-align:center;font-size:10px;text-transform:uppercase;width:40px">#</th>
            <th style="padding:10px;text-align:left;font-size:10px;text-transform:uppercase">Bezeichnung</th>
            <th style="padding:10px;text-align:right;font-size:10px;text-transform:uppercase;width:60px">Menge</th>
            <th style="padding:10px;text-align:center;font-size:10px;text-transform:uppercase;width:70px">Einheit</th>
            <th style="padding:10px;text-align:right;font-size:10px;text-transform:uppercase;width:90px">Einzelpreis</th>
            <th style="padding:10px;text-align:right;font-size:10px;text-transform:uppercase;width:100px">Gesamt</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>

      <div style="display:flex;justify-content:flex-end;margin-bottom:24px">
        <table style="font-size:12px;border-collapse:collapse;min-width:280px">
          <tr>
            <td style="padding:6px 12px;color:#555">Zwischensumme (netto)</td>
            <td style="padding:6px 12px;text-align:right;font-family:monospace">${formatCurrency(subtotal)}</td>
          </tr>
          ${kundenrabattProzent > 0 ? `
          <tr>
            <td style="padding:6px 12px;color:#c2410c;font-weight:600">Kundenrabatt (${kundenrabattProzent} %)</td>
            <td style="padding:6px 12px;text-align:right;font-family:monospace;color:#c2410c;font-weight:600">−${formatCurrency(kundenrabattBetrag)}</td>
          </tr>
          <tr>
            <td style="padding:6px 12px;color:#555">Nettobetrag nach Rabatt</td>
            <td style="padding:6px 12px;text-align:right;font-family:monospace">${formatCurrency(netto)}</td>
          </tr>` : ''}
          ${groups.map((g) => `
          <tr>
            <td style="padding:6px 12px;color:#555">MwSt. ${g.taxRate} %${groups.length > 1 ? ` auf ${formatCurrency(g.net)}` : ''}</td>
            <td style="padding:6px 12px;text-align:right;font-family:monospace">${formatCurrency(g.tax)}</td>
          </tr>`).join('')}
          <tr style="border-top:2px solid #111;background:#f0f9ff">
            <td style="padding:10px 12px;font-weight:700;font-size:13px">Gesamtbetrag</td>
            <td style="padding:10px 12px;text-align:right;font-family:monospace;font-weight:800;font-size:14px;color:#172554">${formatCurrency(total)}</td>
          </tr>
        </table>
      </div>

      ${settings.bank_name || settings.iban ? `
        <div style="margin-top:8px;padding:14px;background:#f0f9ff;border:1px solid #bae6fd;border-radius:6px;font-size:11px">
          <div style="font-size:9px;color:#0c4a6e;font-weight:700;text-transform:uppercase;margin-bottom:6px">Bankverbindung</div>
          ${settings.bank_name ? `<div><strong>Bank:</strong> ${escapeHtml(settings.bank_name)}</div>` : ''}
          ${settings.iban ? `<div><strong>IBAN:</strong> <span style="font-family:monospace">${escapeHtml(settings.iban)}</span></div>` : ''}
          ${settings.bic ? `<div><strong>BIC:</strong> <span style="font-family:monospace">${escapeHtml(settings.bic)}</span></div>` : ''}
          ${ueberweisungHtml ? `<div style="margin-top:6px;color:#555">${ueberweisungHtml}</div>` : ''}
        </div>
      ` : ''}

      ${closingHtml ? `
        <div style="margin-top:20px;font-size:11px;color:#555;line-height:1.6">${closingHtml}</div>
      ` : ''}

      ${renderFooter(settings)}
  `, `Rechnung ${order.invoice_number || ''}`);
}

/**
 * Bedingungen für das PDF: festgeschriebene Rechnung → Werte AUS DER BUCHUNG
 * (Kundenrabatt, Zahlungsziel, Skonto zum Zeitpunkt der Rechnung), sonst
 * aktuelle Kontaktdaten (Entwurf/Vorschau).
 */
async function loadTerms(order) {
  let lead = null;
  if (order.lead_id) {
    try { lead = await base44.entities.Lead.get(order.lead_id); } catch { lead = null; }
  }
  const inv = order._invoice || (order.invoice_number ? await findInvoiceByNo(order.invoice_number) : null);
  if (!inv) return lead;
  return {
    ...(lead || {}),
    kundenrabatt_prozent: Number(inv.kundenrabatt_prozent) || 0,
    zahlungsziel_tage: inv.zahlungsziel_tage ?? lead?.zahlungsziel_tage,
    skonto_prozent: Number(inv.skonto_prozent) || 0,
    skonto_tage: Number(inv.skonto_tage) || 0,
  };
}

/** Synchron (Vorschau mit Musterdaten): Rechnungs-HTML aus Auftrag + Einstellungen + Konditionen. */
export function renderRechnungHtml(order, settings, lead = null) {
  return renderTemplate(order, settings, lead);
}

/** Fatura HTML'ini üretir — önizleme/yazdırma akışları için dışa açık. */
export async function buildRechnungHtml(order, customSettings) {
  const settings = customSettings || await fetchPdfSettings();
  return renderTemplate(order, settings, await loadTerms(order));
}

export async function downloadRechnung(order, customSettings) {
  const settings = customSettings || await fetchPdfSettings();

  // Bedingungen: aus der gebuchten Rechnung, sonst vom Kontakt (Standard: 5 Tage)
  const html = renderTemplate(order, settings, await loadTerms(order));
  const fileName = order.invoice_number || `RE-${Date.now()}`;
  await downloadPdfFromHtml(html, fileName);
}

export default { downloadRechnung, buildRechnungHtml };
